import cx from 'classnames'
import { AxisScale } from 'd3-axis'
import type { ScaleOrdinal } from 'd3-scale'
import { scaleLinear, scaleOrdinal } from 'd3-scale'
import { area, curveMonotoneY, line } from 'd3-shape'
/*
  Based on https://observablehq.com/@d3/line-chart

  TODO(mark): Add graph legend.
*/
import fp, { flatten, flow, get, map, max, merge, min, range, toPairs } from 'lodash/fp'
import D3Chart, {
  type D3ChartAxisOptions,
  D3ChartDomain,
  type D3ChartLayoutOptions,
} from '~/components/d3/D3Chart'
import { D3_SCALE_ORDINAL } from '~/components/d3/vars'
import cs from './d3_line_graph.scss'

// @ts-expect-error: lodash/fp convert isn't properly typed
const forEach = fp.forEach.convert({ cap: false })

interface Point {
  x: number
  y: number
}

export interface D3LineGraphOptions {
  xKey: string
  yKey: string
  dotSize: number
  xDomain: [number, number]
  yDomain: [number, number]
  xDomainMin: number
  xDomainMax: number
  yDomainMin: number
  yDomainMax: number
  lineColor: string | ((key: string) => string)
  isProjected: (key: string) => boolean
  intercept: {
    x: number
    y: number
  }[]
  interceptColor: string
  projectedErrorKey: string
  highlightedLine?: string | null
  // Optional: click callback for points
  onNodeClick?: (
    params: {
      key: string
      timestamp: number
      value: number
      top: number
      left: number
      width: number
      height: number
    } | null,
  ) => void
  // When provided, programmatically display tooltip for the matching node
  selectedTimestamp?: number
}

export type D3LineGraphData = {
  [datasetName: string]: { [key: string]: number }[]
}

// Any additional options for line graph.
const DEFAULT_OPTIONS = {
  xKey: '',
  yKey: '',
  dotSize: 3,
  // The x-axis lower bound will be at least this.
  // Set to null have no lower bound. The bounds will be determined by the data.
  xDomainMin: 0,
  // The x-axis lower bound will be at most this.
  // Set to null have no lower bound. The bounds will be determined by the data.
  xDomainMax: null,
  // The y-axis lower bound will be at least this.
  // Set to null have no lower bound. The bounds will be determined by the data.
  yDomainMin: 0,
  // The y-axis lower bound will be at least this.
  // Set to null have no lower bound. The bounds will be determined by the data.
  yDomainMax: null,
  lineColor: null,
  highlightedLine: null,
}

export default class D3LineGraph extends D3Chart<D3LineGraphData, number, number> {
  chartOptions: Partial<D3LineGraphOptions>
  scaleOrdinal: ScaleOrdinal<string, string>

  constructor(
    container: HTMLDivElement,
    layoutOptions: Partial<D3ChartLayoutOptions> = {},
    axisOptions: Partial<D3ChartAxisOptions<number, number>> = {},
    chartOptions: Partial<D3LineGraphOptions> = {},
  ) {
    super(container, cs.d3LineGraph, layoutOptions, axisOptions)
    this.chartOptions = merge(DEFAULT_OPTIONS, chartOptions)
    this.scaleOrdinal = scaleOrdinal(D3_SCALE_ORDINAL)
  }

  // Nothing to clean-up for this chart.
  teardown = () => {}

  getDomains = (
    data: D3LineGraphData,
  ): { xDomain: D3ChartDomain<number>; yDomain: D3ChartDomain<number> } => {
    let allXValues: number[] = []
    let allYValues: number[] = []
    const options = this.chartOptions

    if (options.xDomain && options.yDomain) {
      return {
        xDomain: options.xDomain as D3ChartDomain<number>,
        yDomain: options.yDomain as D3ChartDomain<number>,
      }
    }

    const xKey = options.xKey as string
    const yKey = options.yKey as string
    forEach(value => {
      allXValues = allXValues.concat(
        map(xKey as any, value as any) as unknown as number[],
      )
      allYValues = allYValues.concat(
        map(yKey as any, value as any) as unknown as number[],
      )
    }, data)

    let xDomainMin = min(allXValues)
    if (options.xDomainMin != null) {
      xDomainMin = min([xDomainMin, options.xDomainMin])
    }

    let xDomainMax = max(allXValues)
    if (options.xDomainMax != null) {
      xDomainMax = max([xDomainMax, options.xDomainMax])
    }

    let yDomainMin = min(allYValues)
    if (options.yDomainMin != null) {
      yDomainMin = min([yDomainMin, options.yDomainMin])
    }

    let yDomainMax = max(allYValues)
    if (options.yDomainMin != null) {
      yDomainMax = max([yDomainMax, options.yDomainMax])
    }

    return {
      xDomain: [xDomainMin || 0, xDomainMax || 0] as D3ChartDomain<number>,
      yDomain: [yDomainMin || 0, yDomainMax || 0] as D3ChartDomain<number>,
    }
  }

  getColor = (key: string) => {
    const options = this.chartOptions
    if (typeof options.lineColor === 'function') {
      return options.lineColor(key)
    }
    return options.lineColor || this.scaleOrdinal(key)
  }

  updateData = (data: D3LineGraphData) => {
    const options = this.chartOptions
    this.dataLayer.selectAll('*').remove()
    this.rerenderAxis(data)

    if (!this.xScale || !this.yScale) return
    const xScale = this.xScale
    const yScale = this.yScale

    const xKey = options.xKey as string
    const yKey = options.yKey as string

    forEach((value, key) => {
      const baseColor = this.getColor(key)
      const isHighlighted = !options.highlightedLine || key === options.highlightedLine
      const color = isHighlighted ? baseColor : '#ccc' // Use grey for non-highlighted lines
      const opacity = 1 // Always show lines, just change color

      const xValues = map(xKey as any, value as any) as unknown as number[]
      const yValues = map(yKey as any, value as any) as unknown as number[]

      if (options.isProjected && options.isProjected(key)) {
        const projectedErrorKey = options.projectedErrorKey as string
        const errorValues = map(
          projectedErrorKey as any,
          value as any,
        ) as unknown as number[]
        const _lineMin = line<number>()
          .curve(curveMonotoneY)
          .x(i => xScale(xValues[i]) || 0)
          .y(i => yScale(yValues[i]) || 0)

        const _area = area<number>()
          .curve(curveMonotoneY)
          .x0(i => xScale(xValues[i]) || 0)
          .x1(i => xScale(xValues[i]) || 0)
          .y0(i => yScale(yValues[i] - errorValues[i]) || 0)
          .y1(i => yScale(yValues[i] + errorValues[i]) || 0)

        this.dataLayer
          .append('path')
          .datum(range(0, value.length))
          .attr('class', cs.area)
          .attr('d', _area)
          .style('opacity', opacity)

        this.dataLayer
          .append('path')
          .attr('class', cs.line)
          .attr('data-key', key)
          .attr('stroke', color)
          .attr('stroke-dasharray', 6)
          .attr('d', _lineMin(range(0, value.length)))
          .style('opacity', opacity)
      } else {
        // curveMonotoneY preserves "monotonocity",
        // meaning the curve won't look like it's going up
        // when in reality the data is staying level.
        const _line = line<number>()
          .curve(curveMonotoneY)
          .x(i => xScale(xValues[i]) || 0)
          .y(i => yScale(yValues[i]) || 0)

        this.dataLayer
          .append('path')
          .attr('class', cs.line)
          .attr('data-key', key)
          .attr('stroke', color)
          .attr('d', _line(range(0, value.length)))
          .style('opacity', opacity)
      }
    }, data)

    const linesSel = this.dataLayer.selectAll<SVGPathElement, unknown>(
      `path.${cs.line}`,
    )

    // De-emphasize non-highlighted and emphasize highlighted
    linesSel
      .style('opacity', function (this: SVGPathElement) {
        const k = this.getAttribute('data-key')
        return !options.highlightedLine || k === options.highlightedLine ? 1 : 0.25
      })
      .attr('stroke-width', function (this: SVGPathElement) {
        const k = this.getAttribute('data-key')
        return !options.highlightedLine || k === options.highlightedLine ? 2.5 : 1.5
      })

    // Ensure highlighted line is on top of the z-order
    linesSel
      .filter(function (this: SVGPathElement) {
        return this.getAttribute('data-key') === options.highlightedLine
      })
      .raise()

    if (options.intercept) {
      forEach(value => {
        const color = options.interceptColor || options.lineColor
        const yIntercept = value.y
        const xIntercept = value.x

        const values = [
          { x: 0, y: yIntercept },
          { x: xIntercept, y: yIntercept },
          // { x: xIntercept, y: 0 },
        ]
        const _line = line<Point>()
          .x(i => xScale(i.x) || 0)
          .y(i => yScale(i.y) || 0)
        this.dataLayer
          .append('path')
          .attr('class', cs.line)
          .attr('stroke', color)
          .attr('d', _line(values))
          .style('stroke-opacity', 0.5)
          .attr('stroke-dasharray', 6)
      }, options.intercept)
    }

    const points = flow(
      toPairs, // [key, [values]]
      map(pairs =>
        map(
          value => ({
            key: pairs[0],
            x: get(xKey as string, value as any) as number,
            y: get(yKey as string, value as any) as number,
          }),
          pairs[1],
        ),
      ),
      flatten,
    )(data)

    const circles = this.dataLayer
      .selectAll<SVGCircleElement, { key: string; x: number; y: number }>('circle')
      .data(points)
      .enter()
      .append('circle')
      .attr('stroke', d => {
        const baseColor = this.getColor(d.key)
        const isHighlighted =
          !options.highlightedLine || d.key === options.highlightedLine
        return isHighlighted ? baseColor : '#999999' // Use grey for non-highlighted points
      })
      .attr('r', options.dotSize)
      .attr('cx', d => xScale(d.x) || null)
      .attr('cy', d => yScale(d.y) || null)
      .attr('class', d =>
        cx(cs.point, options.isProjected && options.isProjected(d.key) && cs.hidden),
      )
      .style('opacity', 1) // Always show points, just change color
      .style('cursor', 'pointer')

    // Hover scrubs: trigger external tooltip (if provided) on native D3 point hover
    const trigger = (this.chartOptions as any).onPointHover as
      | ((
          params:
            | (import('~/components/ChartTooltip').TooltipParams & {
                key: string
                timestamp: number
                value: number
              })
            | null,
        ) => void)
      | undefined

    const selectedTs = (this.chartOptions as any).selectedTimestamp as
      | number
      | null
      | undefined

    if (trigger) {
      circles
        .on(
          'mouseenter',
          (event: MouseEvent, d: { key: string; x: number; y: number }) => {
            const rect = (
              event.currentTarget as SVGCircleElement
            ).getBoundingClientRect()
            trigger({
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              height: rect.height,
              width: rect.width,
              key: d.key,
              timestamp: d.x,
              value: d.y,
            })
          },
        )
        .on(
          'mousemove',
          (event: MouseEvent, d: { key: string; x: number; y: number }) => {
            const rect = (
              event.currentTarget as SVGCircleElement
            ).getBoundingClientRect()
            trigger({
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              height: rect.height,
              width: rect.width,
              key: d.key,
              timestamp: d.x,
              value: d.y,
            })
          },
        )
        .on('mouseleave', () => {
          trigger(null)
        })
    }

    // Click: trigger click handler if provided
    const onNodeClick = (this.chartOptions as any).onNodeClick as
      | ((
          params: {
            key: string
            timestamp: number
            value: number
            top: number
            left: number
            width: number
            height: number
          } | null,
        ) => void)
      | undefined

    if (onNodeClick) {
      // Node click: emit payload and prevent background handler from firing
      circles.on(
        'click',
        (event: MouseEvent, d: { key: string; x: number; y: number }) => {
          event.stopPropagation()
          const rect = (event.currentTarget as SVGCircleElement).getBoundingClientRect()
          onNodeClick({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            height: rect.height,
            width: rect.width,
            key: d.key,
            timestamp: d.x,
            value: d.y,
          })
        },
      )
      // Background click anywhere in the plotting area (non-node): clear selection
      const dim = this.getChartDimensions()
      // Insert a transparent rectangle under all data elements to capture background clicks
      this.dataLayer
        .insert('rect', ':first-child')
        .attr('width', dim.width)
        .attr('height', dim.height)
        .attr('fill', 'transparent')
        .style('pointer-events', 'all')
        .on('click', () => onNodeClick(null))
      // Also attach a click handler on the layer so clicks on paths/areas bubble up
      this.dataLayer.on('click', () => onNodeClick(null))
    }

    // Programmatic tooltip display: if a selected timestamp is provided, show the tooltip
    // on the matching node using the same hover trigger.
    if (selectedTs != null && trigger) {
      // Try exact match first
      let targetPoint = points.find(p => p.x === selectedTs)
      if (!targetPoint) {
        // Fallback to nearest by absolute difference
        targetPoint = points.reduce(
          (acc: Point | null, p) => {
            if (!acc) return p
            return Math.abs(p.x - selectedTs) < Math.abs(acc.x - selectedTs) ? p : acc
          },
          null as Point | null,
        ) as any
      }
      if (targetPoint) {
        const matchSel = circles.filter(
          (d: { key: string; x: number; y: number }) =>
            d.x === targetPoint!.x && d.y === targetPoint!.y,
        )
        const node = matchSel.node()
        if (node) {
          const matchedDatum = matchSel.datum() as {
            key: string
            x: number
            y: number
          }
          const rect = (node as SVGCircleElement).getBoundingClientRect()
          ;(trigger as any)({
            top: rect.top + (window as any).scrollY,
            left: rect.left + (window as any).scrollX,
            height: rect.height,
            width: rect.width,
            key: matchedDatum.key,
            timestamp: matchedDatum.x,
            value: matchedDatum.y,
            __source: 'selected',
          })
        }
      }
    }
  }

  getXScale = (xDomain: D3ChartDomain<number>): AxisScale<number> => {
    const dim = this.getChartDimensions()

    return scaleLinear().domain(xDomain).range([0, dim.width])
  }

  getYScale = (yDomain: [number, number]): AxisScale<number> => {
    const dim = this.getChartDimensions()

    return scaleLinear().domain(yDomain).range([dim.height, 0])
  }

  updateChartOptions = (options: unknown) => {
    this.chartOptions = merge(DEFAULT_OPTIONS, options as Partial<D3LineGraphOptions>)
    if (this._data) {
      this.updateData(this._data)
    }
  }
}
